<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useContactNotes } from 'dashboard/composables/useContactNotes';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactSummaryCard from 'dashboard/components-next/Contacts/ContactDetail/ContactSummaryCard.vue';
import ContactOverviewTab from 'dashboard/components-next/Contacts/ContactDetail/ContactOverviewTab.vue';
import ContactConversationsTab from 'dashboard/components-next/Contacts/ContactDetail/ContactConversationsTab.vue';
import ContactNotesTab from 'dashboard/components-next/Contacts/ContactDetail/ContactNotesTab.vue';
import ContactMergeDialog from 'dashboard/components-next/Contacts/ContactDetail/ContactMergeDialog.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');
const conversationsByContact = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const activeTab = ref('overview');
const mergeDialogRef = ref(null);

const contactId = computed(() => route.params.contactId);

// El contador de la pestaña cuenta lo mismo que se ve dentro: notas de
// contacto más notas privadas de sus conversaciones.
const { allNotes } = useContactNotes(contactId);
const selectedContact = computed(() => contact.value(contactId.value));

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);
const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const tabs = computed(() => [
  {
    value: 'overview',
    label: t('CONTACTS_LAYOUT.DETAILS.TABS.OVERVIEW'),
    count: 0,
  },
  {
    value: 'conversations',
    label: t('CONTACTS_LAYOUT.DETAILS.TABS.CONVERSATIONS'),
    count: (conversationsByContact.value(contactId.value) || []).length,
  },
  {
    value: 'notes',
    label: t('CONTACTS_LAYOUT.DETAILS.TABS.NOTES'),
    count: allNotes.value.length,
  },
]);

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchContactData = async () => {
  const id = contactId.value;
  if (!id) return;
  activeTab.value = 'overview';
  store.dispatch('attributes/get');
  store.dispatch('contactNotes/get', { contactId: id });
  store.dispatch('contactConversations/get', id);
  store.dispatch('contactTasks/get', { contactId: id });
  await store.dispatch('contacts/show', { id });
  await store.dispatch('contacts/fetchContactableInbox', id);
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch('contacts/update', {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

const openMergeDialog = () => mergeDialogRef.value?.open();

watch(contactId, fetchContactData);
onMounted(fetchContactData);
</script>

<template>
  <ContactsDetailsLayout
    :selected-contact="selectedContact"
    :is-updating="isUpdatingContact"
    @go-to-contacts-list="goToContactsList"
    @toggle-block="toggleContactBlock"
    @merge="openMergeDialog"
  >
    <template v-if="selectedContact?.id && !showSpinner" #aside>
      <ContactSummaryCard :contact="selectedContact" />
    </template>

    <div
      v-if="showSpinner || !selectedContact?.id"
      class="flex items-center justify-center py-10 text-n-slate-11 lg:col-span-2"
    >
      <Spinner />
    </div>
    <template v-else>
      <div class="flex items-center gap-5 border-b border-n-weak">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="flex items-center gap-1.5 pb-2.5 -mb-px text-[13px] border-b-2 transition-colors"
          :class="
            activeTab === tab.value
              ? 'font-semibold text-n-slate-12 border-n-brand'
              : 'font-medium text-n-slate-10 border-transparent hover:text-n-slate-12'
          "
          @click="activeTab = tab.value"
        >
          <span>{{ tab.label }}</span>
          <span
            v-if="tab.count"
            class="px-1.5 py-px rounded-full text-[11px] font-semibold tabular-nums"
            :class="
              activeTab === tab.value
                ? 'bg-n-blue-2 text-n-blue-text'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            {{ tab.count }}
          </span>
        </button>
      </div>

      <ContactOverviewTab
        v-if="activeTab === 'overview'"
        :contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      <ContactConversationsTab
        v-else-if="activeTab === 'conversations'"
        :contact="selectedContact"
      />
      <ContactNotesTab v-else :contact="selectedContact" />

      <ContactMergeDialog
        ref="mergeDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
    </template>
  </ContactsDetailsLayout>
</template>
